import { getVueAppBaseApi } from '@/services/util';

export const UI_LANG_STORAGE_KEY = 'arco-lang';

export type UiLocaleItem = { value: string; label: string };

/** 前端已接好的 UI 语言。Nacos 只能在这个名单里做开关。 */
export const DEFAULT_UI_LOCALES: UiLocaleItem[] = [
  { value: 'zh-CN', label: '中文' },
  { value: 'en-US', label: 'English' },
  { value: 'vi-VN', label: 'Tiếng Việt' },
];

export const UI_LOCALES = DEFAULT_UI_LOCALES;

let enabledLocales: UiLocaleItem[] = DEFAULT_UI_LOCALES.map((item) => ({ ...item }));

export function getUiLocales(): UiLocaleItem[] {
  return enabledLocales;
}

function knownLabel(code: string) {
  const hit = DEFAULT_UI_LOCALES.find((item) => item.value === code);
  return hit ? hit.label : code;
}

function isKnownLocale(code: string) {
  return DEFAULT_UI_LOCALES.some((item) => item.value === code);
}

function enabledCodes() {
  return enabledLocales.map((item) => item.value);
}

function fallbackLocale(): string {
  const codes = enabledCodes();
  if (codes.indexOf('zh-CN') >= 0) {
    return 'zh-CN';
  }
  return codes[0] || 'zh-CN';
}

type MetaLocaleItem = string | { code?: string; value?: string; label?: string };
type MetaPayload = {
  locales?: MetaLocaleItem[];
  data?: { locales?: MetaLocaleItem[] };
};

export function applyUiLocalesFromMeta(payload?: MetaPayload | null) {
  const rawList = payload && Array.isArray(payload.locales)
    ? payload.locales
    : payload && payload.data && Array.isArray(payload.data.locales)
      ? payload.data.locales
      : null;
  if (!rawList || !rawList.length) {
    return;
  }
  const next: UiLocaleItem[] = [];
  rawList.forEach((item) => {
    const code = typeof item === 'string' ? item : item && (item.code || item.value);
    const label = typeof item === 'object' && item ? item.label : '';
    if (!code || !isKnownLocale(code)) {
      return;
    }
    next.push({
      value: code,
      label: label ? String(label) : knownLabel(code),
    });
  });
  if (!next.length) {
    return;
  }
  enabledLocales = next;
  persistUiLocale(getUiLocale());
}

export function normalizeUiLocale(input?: string | null): string {
  const raw = String(input || '').trim();
  const codes = enabledCodes();
  if (codes.indexOf(raw) >= 0) {
    return raw;
  }
  const lower = raw.toLowerCase();
  if (lower.startsWith('zh') && codes.indexOf('zh-CN') >= 0) {
    return 'zh-CN';
  }
  if (lower.startsWith('vi') && codes.indexOf('vi-VN') >= 0) {
    return 'vi-VN';
  }
  if (lower.startsWith('en') && codes.indexOf('en-US') >= 0) {
    return 'en-US';
  }
  return fallbackLocale();
}

export function getUiLocale(): string {
  try {
    return normalizeUiLocale(localStorage.getItem(UI_LANG_STORAGE_KEY));
  } catch (e) {
    return fallbackLocale();
  }
}

export function applyDocumentUiLang(locale?: string) {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.lang = normalizeUiLocale(locale || getUiLocale());
}

export function persistUiLocale(locale: string) {
  const next = normalizeUiLocale(locale);
  try {
    localStorage.setItem(UI_LANG_STORAGE_KEY, next);
  } catch (e) {
    // ignore
  }
  try {
    document.cookie = `ui-locale=${next};path=/;max-age=31536000`;
  } catch (e) {
    // ignore
  }
  applyDocumentUiLang(next);
  return next;
}

export function switchUiLocale(locale: string) {
  persistUiLocale(locale);
  window.location.reload();
}

if (typeof document !== 'undefined') {
  applyDocumentUiLang();
}

export async function bootstrapUiLocaleMeta() {
  try {
    const root = String(getVueAppBaseApi() || '').replace(/\/$/, '');
    if (!root) {
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 1500);
    let res: Response;
    try {
      res = await fetch(`${root}/cartsee/i18n/meta`, {
        credentials: 'omit',
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timer);
    }
    if (!res.ok) {
      return;
    }
    const payload = await res.json();
    if (payload && typeof payload.code === 'number' && payload.code !== 0) {
      return;
    }
    applyUiLocalesFromMeta(payload && payload.data !== undefined ? payload.data : payload);
  } catch (e) {
    // 导航栏回退到前端写死的中/英/越
  }
}
